import { locateProxy } from '#~/agent/index.js';
import type { OmitSyncProps } from '#~/api/types.js';
import { StudioError } from '#~/error/index.js';
// Gating (spec §6): a transitory placeholder must do NOTHING (no recording,
// no RTMP) until authenticated. Per `reference-rules-in-comments` (global).
import { isTransitoryId } from '#~/device/gating.js';
import type { Device } from '@studio/models/device';
import type { ControlDevice } from '@tryiris-ai/common.protojs/agent/v1/agent';

import { BILLING_LIMIT_RTMP_ERROR, isBillingLimitError } from '../rtmp/billing-limit.js';
import type { Destination } from '../rtmp/store/destinations.js';
import { StreamOperation } from '../rtmp/store/destinations.js';
import type { ProfileConfig } from '../rtmp/utils.js';
import { audioCodecsToEgressCodec, videoCodecsToEgressCodec } from '../rtmp/utils.js';

/**
 * Sets the status of a device, including recording and RTMP streaming options.
 *
 * @param {Device['id']} deviceId - The ID of the device to update.
 * @param {boolean} includeAudio - Whether to include audio in the operation.
 * @param {Object} options - Configuration options for the device.
 * @param {boolean} [options.record] - Whether to enable recording.
 * @param {Object} [options.rtmp] - RTMP streaming options.
 * @param {boolean} options.rtmp.enable - Whether to enable RTMP streaming.
 * @param {string} options.rtmp.destinationId - The ID of the RTMP destination.
 * @param {ProfileConfig} [options.rtmp.profileConfig] - The stream profile configuration.
 * @returns {Promise<void>} Resolves when the device status has been set.
 * @throws {StudioError} If the device proxy cannot be found.
 */
export const setDeviceStatus = async (
  deviceId: Device['id'],
  includeAudio: boolean,
  {
    record,
    rtmp
  }: {
    record?: boolean;
    rtmp?: {
      enable: boolean;
      destination: Destination;
      profileConfig: ProfileConfig;
      operation?: StreamOperation;
    };
  }
): Promise<void> => {
  // Gating §6 — recording/RTMP capability over a transitory placeholder.
  // A placeholder has no proxy, so the `!proxy` branch below would already
  // THROW; this defensive bail makes it a clean no-op instead (a placeholder
  // is simply not a recordable/streamable source — spec §6 "no puede hacer
  // ninguna acción hasta autenticar").
  if (isTransitoryId(deviceId)) {
    return;
  }

  const proxy = locateProxy(deviceId);

  if (!proxy) {
    throw new StudioError('Device proxy not found. Make sure the device is connected.');
  }

  const { url = '', streamKey = '', nickName = '' } = rtmp?.destination ?? {};

  const config: OmitSyncProps<Pick<ControlDevice, 'egress'>> = {
    egress: {
      ndiOutput: undefined,
      recording:
        record !== undefined
          ? {
              enable: record,
              includeAudio
            }
          : undefined,
      rtmp:
        rtmp !== undefined
          ? {
              rtmpOptions: {
                width: rtmp.profileConfig.width,
                height: rtmp.profileConfig.height,
                depth: rtmp.profileConfig.depth,
                framerate: rtmp.profileConfig.frameRate,
                audioCodec: audioCodecsToEgressCodec[rtmp.profileConfig.audioCodec],
                audioBitrate: rtmp.profileConfig.audioBitrate,
                audioFrequency: rtmp.profileConfig.audioFrequency,
                videoCodec: videoCodecsToEgressCodec[rtmp.profileConfig.videoCodec],
                keyFrameInterval: rtmp.profileConfig.keyFrameInterval,
                audioQuality: 1,
                videoQuality: 1,
                videoBitrate: rtmp.profileConfig.videoBitrate
              },
              destinations: {
                [rtmp.destination.id]: {
                  enable: rtmp.enable,
                  destinationOptions: {
                    url,
                    streamKey,
                    nickName
                  }
                }
              }
            }
          : undefined
    }
  };

  // If the user doesn't set a sink name, we'll use the default source name.
  const mutation = {
    control: {
      device: {
        [deviceId]: config
      }
    }
  };

  try {
    if (!rtmp?.operation) {
      await proxy.mutate(mutation, {});
      return;
    }

    if ([StreamOperation.Start].includes(rtmp.operation)) {
      await proxy.mutate(mutation, {});
    }

    if ([StreamOperation.Stop].includes(rtmp.operation)) {
      await proxy.mutate({}, mutation);
    }
  } catch (cause) {
    if (rtmp?.operation === StreamOperation.Start && isBillingLimitError(cause)) {
      throw new StudioError(BILLING_LIMIT_RTMP_ERROR, { cause });
    }

    throw new StudioError('Unable to egress video.', { cause });
  }
};
